/**
 * TripMap — one Google Map component, three modes, both modules.
 *
 * Modes:
 *   • "planned" — draws the ROUTE polyline (from Google Directions API
 *                 encoded polyline) plus daily-stop / charger / fuel
 *                 pins. Powers /roadtrip planning view.
 *   • "trail"   — draws the BREADCRUMB polyline of past check-ins for
 *                 (source, key). Every check-in is a numbered pin.
 *                 Powers "where has the traveller been" AND the
 *                 HustleFinder listing's "recent visitors" map.
 *   • "combined"— shows both: the planned route (thin amber) UNDER the
 *                 traveled trail (thick blue), plus every planned pin.
 *                 Powers the RoadtripDrivePage live map.
 *
 * Pin taxonomy (colour codes are stable across every page):
 *   🟠 amber   — planned daily stop (accommodation / attraction)
 *   ⚡ green   — EV charger stop (OpenChargeMap or Tesla)
 *   ⛽ blue    — fuel / rest stop
 *   📍 red    — traveller current location (live)
 *   ⚫ dark    — past check-in from the trail
 *
 * Owner rule #4: ONE component, every module. Import it from
 * `@/components/TripMap`. Do NOT re-implement the Google Map init.
 */
import { useEffect, useRef, useState } from "react";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/** Idempotent Google Maps JS SDK loader — shared with CatchmentMap. */
function loadGoogleMaps(apiKey) {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (window.__hpGmapsLoading) return window.__hpGmapsLoading;
  window.gm_authFailure = () => {
    window.__hpGmapsAuthFailed = true;
    try { window.dispatchEvent(new CustomEvent("hp-gmaps-auth-failure")); }
    catch (_) { /* noop */ }
  };
  window.__hpGmapsLoading = new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=&v=quarterly`;
    s.async = true;
    s.defer = true;
    s.onload  = () => res(window.google.maps);
    s.onerror = () => rej(new Error("Google Maps SDK failed to load"));
    document.head.appendChild(s);
  });
  return window.__hpGmapsLoading;
}

/** Decode a Google-encoded polyline string into [{lat,lng}, ...]. */
function decodePolyline(str) {
  if (!str) return [];
  const pts = [];
  let idx = 0, lat = 0, lng = 0;
  while (idx < str.length) {
    let b, shift = 0, result = 0;
    do { b = str.charCodeAt(idx++) - 63;
         result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { b = str.charCodeAt(idx++) - 63;
         result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    pts.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return pts;
}

/** Build an SVG data-URL pin in the requested colour. Fast, cache-
 *  friendly, avoids Google Marker deprecation warnings. */
function pinIcon(fill = "#f59e0b", stroke = "#0c0a09", size = 34) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 34 34">
      <path d="M17 2C10.4 2 5 7.4 5 14c0 8.6 12 18 12 18s12-9.4 12-18c0-6.6-5.4-12-12-12z"
            fill="${fill}" stroke="${stroke}" stroke-width="1.6"/>
      <circle cx="17" cy="14" r="4.2" fill="${stroke}"/>
    </svg>`.trim();
  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
}

/* Pin variants by role. */
const PIN = {
  stop:      { fill: "#f59e0b", stroke: "#0c0a09" }, // amber
  charger:   { fill: "#10b981", stroke: "#053024" }, // green
  fuel:      { fill: "#3b82f6", stroke: "#0b1f4a" }, // blue
  live:      { fill: "#ef4444", stroke: "#7a0e0e" }, // red
  breadcrumb:{ fill: "#57534e", stroke: "#0c0a09" }, // stone
};


export default function TripMap({
  mode         = "planned",   // "planned" | "trail" | "combined"
  polyline     = "",           // encoded polyline (planned route)
  stops        = [],           // [{lat,lng,label,kind}]  kind ∈ PIN keys
  trail        = [],           // [{lat,lng,address,place_name,created_at}]
  liveLocation = null,         // {lat,lng} traveller's current position
  height       = 420,
  onPinClick,                  // (item, kind) => void
  testid       = "trip-map",
}) {
  const ref  = useRef(null);
  const mapRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [err,   setErr]   = useState(null);
  const [authFail, setAuthFail] = useState(
    typeof window !== "undefined" && !!window.__hpGmapsAuthFailed);
  const [selected, setSelected] = useState(null);

  // Auth-failure listener (mirrors CatchmentMap).
  useEffect(() => {
    const on = () => { setAuthFail(true);
                        if (ref.current) ref.current.innerHTML = ""; };
    window.addEventListener("hp-gmaps-auth-failure", on);
    return () => window.removeEventListener("hp-gmaps-auth-failure", on);
  }, []);

  // Boot the map + draw everything each time inputs change. We rebuild
  // the overlays (not the map instance) so switching pages doesn't
  // flash-load the SDK.
  useEffect(() => {
    if (!KEY || authFail || !ref.current) return;
    let cancelled = false;
    const overlays = { markers: [], polylines: [] };

    (async () => {
      try {
        const maps = await loadGoogleMaps(KEY);
        if (cancelled) return;
        if (window.__hpGmapsAuthFailed) { setAuthFail(true); return; }

        // Build map instance once per component mount.
        if (!mapRef.current) {
          mapRef.current = new maps.Map(ref.current, {
            zoom: 5,
            center: { lat: -25.3, lng: 134.5 },  // rough centre of AU
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            styles: [
              { featureType: "poi.business",
                 stylers: [{ visibility: "off" }] },
              { featureType: "transit",
                 stylers: [{ visibility: "simplified" }] },
            ],
          });
        }
        const map = mapRef.current;
        const bounds = new maps.LatLngBounds();
        const info   = new maps.InfoWindow();

        const addPin = (lat, lng, kind, title, payload) => {
          if (!isFinite(lat) || !isFinite(lng)) return;
          const cfg = PIN[kind] || PIN.stop;
          const m = new maps.Marker({
            position: { lat, lng },
            map,
            title,
            icon: {
              url: pinIcon(cfg.fill, cfg.stroke, 34),
              scaledSize: new maps.Size(34, 34),
              anchor:     new maps.Point(17, 32),
            },
          });
          m.addListener("click", () => {
            const body = payload?.address || title || "";
            const sub  = payload?.created_at
              ? new Date(payload.created_at).toLocaleString()
              : (payload?.subtitle || "");
            info.setContent(`
              <div style="font:600 13px Inter,sans-serif;color:#0c0a09;
                            padding:2px 6px;max-width:260px;">
                <div style="text-transform:uppercase;font-size:10px;
                             letter-spacing:0.08em;color:${cfg.fill};">
                  ${kind}
                </div>
                <div style="font-size:14px;margin:4px 0;">${title || "Pin"}</div>
                ${body ? `<div style="font-weight:400;color:#57534e;
                                        font-size:12px;">${body}</div>` : ""}
                ${sub  ? `<div style="font-weight:400;color:#8a8378;
                                        font-size:11px;margin-top:3px;">
                             ${sub}</div>` : ""}
              </div>`);
            info.open({ map, anchor: m });
            setSelected({ ...payload, kind, title });
            onPinClick?.(payload || { lat, lng, title }, kind);
          });
          overlays.markers.push(m);
          bounds.extend({ lat, lng });
        };

        // Planned route polyline (amber, semi-transparent when in
        // combined mode so the trail sits crisply on top).
        if ((mode === "planned" || mode === "combined") && polyline) {
          const pts = decodePolyline(polyline);
          if (pts.length > 1) {
            const p = new maps.Polyline({
              path: pts,
              geodesic: true,
              strokeColor:   "#f59e0b",
              strokeOpacity: mode === "combined" ? 0.55 : 0.9,
              strokeWeight:  mode === "combined" ? 4 : 5,
              map,
            });
            overlays.polylines.push(p);
            pts.forEach((pt) => bounds.extend(pt));
          }
        }

        // Trail polyline (blue, drawn on top).
        if ((mode === "trail" || mode === "combined") && trail.length > 1) {
          const pts = trail.map((t) => ({ lat: t.lat, lng: t.lng }));
          const p = new maps.Polyline({
            path: pts,
            strokeColor:   "#2563eb",
            strokeOpacity: 0.95,
            strokeWeight:  4,
            icons: [{                  // little chevrons showing direction
              icon: { path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
                       scale: 3, strokeColor: "#1d4ed8" },
              offset: "40px", repeat: "80px",
            }],
            map,
          });
          overlays.polylines.push(p);
        }

        // Planned stops (chargers / fuel / lodging).
        if (mode === "planned" || mode === "combined") {
          stops.forEach((s) => {
            const kind = PIN[s.kind] ? s.kind : "stop";
            addPin(s.lat, s.lng, kind,
                    s.label || s.title || s.name || "",
                    s);
          });
        }

        // Trail pins (breadcrumb dots).
        if (mode === "trail" || mode === "combined") {
          trail.forEach((t, i) => {
            addPin(t.lat, t.lng, "breadcrumb",
                    `${i + 1}. ${t.place_name || "Check-in"}`, t);
          });
        }

        // Live location — always drawn last so it stacks on top.
        if (liveLocation?.lat && liveLocation?.lng) {
          addPin(liveLocation.lat, liveLocation.lng, "live",
                  liveLocation.title || "You are here",
                  liveLocation);
        }

        // Frame everything nicely.
        if (!bounds.isEmpty()) {
          if (overlays.markers.length === 1 && !polyline && !trail.length) {
            map.setCenter(bounds.getCenter());
            map.setZoom(14);
          } else {
            map.fitBounds(bounds, 60);
          }
        }
        setReady(true);
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      }
    })();

    return () => {
      cancelled = true;
      overlays.markers.forEach((m) => m.setMap(null));
      overlays.polylines.forEach((p) => p.setMap(null));
    };
  }, [mode, polyline, JSON.stringify(stops), JSON.stringify(trail),
       liveLocation?.lat, liveLocation?.lng, authFail]);

  // Fallback UI — key missing / auth failure / SDK error. Still shows
  // the pin list so the data survives even when the canvas can't render.
  if (!KEY || err || authFail) {
    return (
      <div data-testid={`${testid}-fallback`}
            className="w-full rounded-2xl border border-stone-200 dark:border-stone-700
                       bg-stone-100 dark:bg-stone-900 p-4 text-sm space-y-3"
            style={{ minHeight: height }}>
        <div className="font-semibold">Map preview unavailable</div>
        <div className="text-stone-500 text-xs">
          {!KEY  && "Google Maps API key not configured."}
          {authFail && "Google Maps rejected our key. Check billing / API enabled."}
          {err   && `Error: ${err}`}
        </div>
        {(stops?.length > 0 || trail?.length > 0) && (
          <ul className="text-xs space-y-1 text-stone-600 dark:text-stone-300">
            {stops.map((s, i) => (
              <li key={`s${i}`}>• {s.label || s.title || s.name}
                 <span className="text-stone-400 ml-1">
                   ({(s.lat || 0).toFixed(3)}, {(s.lng || 0).toFixed(3)})
                 </span></li>
            ))}
            {trail.map((t, i) => (
              <li key={`t${i}`}>◦ {t.place_name || t.address}
                 <span className="text-stone-400 ml-1">
                   ({new Date(t.created_at).toLocaleString()})
                 </span></li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="hp-trip-map relative" data-testid={testid}>
      <div ref={ref}
            style={{ width: "100%", height,
                     borderRadius: "1rem",
                     background: "#eee" }}
            data-testid={`${testid}-canvas`}/>
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center
                         text-sm text-stone-500 pointer-events-none">
          Loading map…
        </div>
      )}
    </div>
  );
}
